/**
 * Builds a region-aware URL by inserting the region slug into the path
 * 
 * Note: (dashboard) is a route group, so it doesn't appear in the URL.
 * The actual URL structure is /[region]/[path], not /dashboard/[region]/[path]
 * 
 * @param regionSlug - The region slug (e.g., "north-carolina")
 * @param path - The path without region (e.g., "/reports" or "/home")
 * @param params - Optional query parameters
 * @returns The full URL with region segment (e.g., "/north-carolina/reports?page=1")
 * 
 * @example
 * buildRegionUrl("north-carolina", "/reports", { page: 1 })
 * // returns "/north-carolina/reports?page=1"
 * 
 * buildRegionUrl("north-carolina", "/home")
 * // returns "/north-carolina/home"
 */
type QueryParamValue = string | number | boolean | string[]

function isValidQueryParamValue(value: unknown): value is QueryParamValue {
  if (value === undefined || value === null) return false
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === 'string') {
    return value !== ''
  }
  return typeof value === 'number' || typeof value === 'boolean'
}

export function buildRegionUrl(
  regionSlug: string | null,
  path: string,
  params?: Record<string, string | undefined | null | number | boolean | string[]>
): string {
  // Remove leading slash from path if present (we'll add it back)
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  
  // If no region, return the path as-is (for backward compatibility during migration)
  if (!regionSlug) {
    const queryString = params
      ? Object.entries(params)
          .filter(([_, value]) => isValidQueryParamValue(value))
          .flatMap(([key, value]) => {
            if (Array.isArray(value)) {
              return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`)
            }
            return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`]
          })
          .join('&')
      : ''
    
    return queryString ? `/${cleanPath}?${queryString}` : `/${cleanPath}`
  }
  
  // Build the region-aware path: /[region]/[path]
  // Note: (dashboard) is a route group, so it doesn't appear in the URL
  const regionPath = `/${regionSlug}/${cleanPath}`
  
  // Add query parameters if provided
  if (params) {
    const queryString = Object.entries(params)
      .filter(([_, value]) => isValidQueryParamValue(value))
      .flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`)
        }
        return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`]
      })
      .join('&')
    
    return queryString ? `${regionPath}?${queryString}` : regionPath
  }
  
  return regionPath
}

export function buildRegionAwareHref(regionSlug: string | null, href: string): string {
  if (!regionSlug) {
    return href
  }

  if (href.startsWith(`/${regionSlug}/`) || href === `/${regionSlug}` || href.startsWith(`/${regionSlug}?`)) {
    return href
  }

  const [pathOnly, queryString] = href.split('?')
  if (queryString) {
    const params = Object.fromEntries(new URLSearchParams(queryString))
    return buildRegionUrl(regionSlug, pathOnly, params)
  }

  return buildRegionUrl(regionSlug, href)
}

/**
 * Removes the region segment from a URL path
 * 
 * Note: (dashboard) is a route group, so it doesn't appear in the URL.
 * The actual URL structure is /[region]/[path], not /dashboard/[region]/[path]
 * 
 * @param path - The full path with region (e.g., "/north-carolina/reports")
 * @returns The path without region (e.g., "/reports")
 * 
 * @example
 * removeRegionFromPath("/north-carolina/reports")
 * // returns "/reports"
 */
export function removeRegionFromPath(path: string): string {
  // Match pattern: /[region]/[rest-of-path]
  // Note: (dashboard) is a route group, so it doesn't appear in the URL
  const match = path.match(/^\/[^/]+(\/.*)?$/)
  
  if (match) {
    // Return the path after the region segment
    return match[1] || '/'
  }
  
  // If pattern doesn't match, return as-is
  return path
}

